import React, { useState } from 'react';
import { AuthProvider, useAuth, ProtectedRoute } from './contexts/AuthContext';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import MainContent from './components/layout/MainContent';
import Footer from './components/layout/Footer';
import Login from './components/auth/Login';
import LayoutTestPage from './components/test/LayoutTestPage';
import './styles/global.css'; // Global design system
import './App.css';
import './styles/tabActions.css';
import WhistleBlowerBubble from './components/common/WhistleBlowerBubble';
import LeadershipPage from './pages/LeadershipPage';
import MemberDashboard from './views/member/MemberDashboard';
import AnalystDashboard from './views/analyst/AnalystDashboard';
import { ROLE_C_SUITE, ROLE_ORG_MEMBER, ROLE_RISK_ANALYST } from './services/userService';

// Main application component wrapped with authentication
function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [showLayoutTest, setShowLayoutTest] = useState(false);
  const [showLayoutTest2, setShowLayoutTest2] = useState(false);
  const { state } = useAuth();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMenuSelect = (menuId: string) => {
    setActiveMenu(menuId);
    // Close sidebar on mobile after menu selection
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const handleLayoutTestToggle = () => {
    setShowLayoutTest(true);
  };

  const handleLayoutTest2Click = () => {
    // Open Page 2 in new tab by creating a new window with the component
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) {
      // Create a simple HTML page with inline CSS and the component content
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Body Grid Isolation Test</title>
          <style>
            body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
            .full-area { display: flex; flex-direction: column; height: 100vh; background: #f5f7fa; overflow: hidden; box-sizing: border-box; }
            .header-row { height: 70px; width: 100%; background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%); color: white; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; flex-shrink: 0; box-sizing: border-box; border: 3px solid red; }
            .body-grid { display: grid; grid-template-columns: 60px 1fr; gap: 0; padding: 0; margin: 0; height: 100%; width: 100%; box-sizing: border-box; flex: 1; border: 3px solid blue; }
            .menu-area { width: 100%; background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%); color: white; display: flex; align-items: center; justify-content: center; height: 100%; margin: 0; padding: 0; position: relative; box-sizing: border-box; }
            .page-area { display: flex; flex-direction: column; gap: 16px; min-width: 0; margin: 0; padding: 16px; background: #f5f7fa; height: 100%; overflow-y: auto; }
          </style>
        </head>
        <body>
          <div class="full-area">
            <div class="header-row">
              <h1 style="margin: 0; font-size: 24px; font-weight: 700; color: white;">🏗️ Test Page 2</h1>
              <span style="font-size: 12px; color: rgba(255,255,255,0.8);">Width Comparison Test</span>
            </div>
            <div class="body-grid">
              <div class="menu-area">📄</div>
              <div class="page-area">
                <h2>Width Comparison Test</h2>
                <p>Now with header structure matching Test Page 1.</p>
                <p>Does the blue border behave the same?</p>
                <p>Blue border shows exact .body-grid boundaries.</p>
              </div>
            </div>
          </div>
        </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleBackToApp = () => {
    setShowLayoutTest(false);
  };

  if (showLayoutTest) {
    return (
      <div className="App">
        <ProtectedRoute fallback={<Login />}>
          <LayoutTestPage onBackToApp={handleBackToApp} />
        </ProtectedRoute>
      </div>
    );
  }

  // Helper to get numeric role
  const getRole = () => {
    const role = state.user?.role;
    if (typeof role === 'number') return role;
    if (typeof role === 'string') return parseInt(role, 10);
    // Fallback to localStorage if state is not yet ready but we have a token
    const storedRole = localStorage.getItem('anygrc_user_role');
    if (storedRole) return parseInt(storedRole, 10);
    return null;
  };

  const userRole = getRole();

  // Special layout for C-Suite
  if (userRole === ROLE_C_SUITE) {
    return (
      <div className="App">
        <ProtectedRoute fallback={<Login />}>
          <LeadershipPage />
        </ProtectedRoute>
      </div>
    );
  }

  // Special layout for Org Member (Staff)
  if (userRole === ROLE_ORG_MEMBER) {
    return (
      <div className="App">
        <ProtectedRoute fallback={<Login />}>
          <MemberDashboard />
        </ProtectedRoute>
      </div>
    );
  }

  // Special layout for Risk Analyst
  if (userRole === ROLE_RISK_ANALYST) {
    return (
      <div className="App">
        <ProtectedRoute fallback={<Login />}>
          <AnalystDashboard />
        </ProtectedRoute>
      </div>
    );
  }

  return (
    <div className="App">
      <ProtectedRoute fallback={<Login />}>
        <>
          <Header
            onToggleSidebar={toggleSidebar}
            sidebarOpen={sidebarOpen}
            onLogoClick={() => setActiveMenu('dashboard')}
            onLayoutTestClick={handleLayoutTestToggle}
            onLayoutTest2Click={handleLayoutTest2Click}
          />

          <Sidebar
            isOpen={sidebarOpen}
            onMenuSelect={handleMenuSelect}
            activeMenu={activeMenu}
            onToggle={toggleSidebar}
          />

          <MainContent
            activeMenu={activeMenu}
            sidebarOpen={sidebarOpen}
            onNavigate={handleMenuSelect}
            onToggleSidebar={toggleSidebar}
          />

          <Footer />
          <WhistleBlowerBubble />
        </>
      </ProtectedRoute>
    </div>
  );
}

// Root App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
